import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { BRAND } from "@/lib/content";

const LINKS = [
  { label: "Your rights", href: "#coercion" },
  { label: "My story", href: "#story" },
  { label: "Get to safety", href: "#safety" },
  { label: "Find help", href: "#directory" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-[65] transition-colors duration-500 ${
        scrolled ? "bg-bone/85 backdrop-blur-md border-b border-line" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a
          href="#top"
          data-testid="nav-logo"
          className="max-w-[62%] font-serif text-xl font-medium leading-tight tracking-tight text-ink sm:text-2xl md:max-w-none"
        >
          {BRAND.name}
          <span className="text-terracotta">.</span>
        </a>

        <div className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="group relative text-sm font-medium text-stone transition-colors hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-terracotta transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <a
          href="#hotlines"
          data-testid="nav-call-cta"
          className="hidden items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-bone transition-transform duration-300 hover:scale-105 md:inline-flex"
        >
          <Phone className="h-4 w-4" strokeWidth={2.2} />
          Talk to someone
        </a>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 md:hidden"
        >
          <span className={`h-0.5 w-6 bg-ink transition-transform ${open ? "translate-y-2 rotate-45" : ""}`} />
          <span className={`h-0.5 w-6 bg-ink transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`h-0.5 w-6 bg-ink transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`} />
        </button>
      </nav>

      {open && (
        <div className="border-t border-line bg-bone px-5 py-4 md:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-ink"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#hotlines"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-bone"
            >
              <Phone className="h-4 w-4" /> Talk to someone
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
